import type { CustomBlockDeployedEntity } from '@unifyapps/defs/blocks/Custom/types';
import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import { useBulkFetchEntitySnapshots } from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import { useMemo } from 'react';
import { collectCustomBlockVersions, getCustomBlocksEntitySnapshotsFetchRequest } from './utils';

/**
 * Snapshots-only multi-page variant. Fires ONLY useBulkFetchEntitySnapshots for the
 * union of customBlocksVersionsMap entries across pages — no entity-level search.
 * Used by Standard (builder/preview) which renders from snapshots and never reads
 * the entity-level `customBlocks` list, so the entity search would be wasted.
 */
export function useFetchCustomBlockSnapshotsForPages(pages: (InterfacePageEntity | undefined)[]) {
  const customBlockVersions = useMemo(() => collectCustomBlockVersions(pages), [pages]);

  return useBulkFetchEntitySnapshots(
    getCustomBlocksEntitySnapshotsFetchRequest(customBlockVersions),
    {
      query: {
        select: (data) =>
          Object.values(data.entityTypeVsEntities?.[UAEntityTypes.CustomBlock] || {}) as
            | CustomBlockDeployedEntity[]
            | undefined,
        enabled: customBlockVersions.length > 0,
        staleTime: Infinity,
      },
    },
  );
}
